import React, {useState, useEffect, useRef, useCallback} from "react";

// Section for Utilities and Context
import "./directory.css";
import API from "../utils/API";

// Section for Components
import DirectoryCard from "../components/DirectoryCard";
import OrganizationCard from "../components/OrganizationCard";
import PageTitle from "../components/PageTitle";
import { isDistrictFiller, matchesDistrict, normalizeDistrictQuery } from "../utils/district";

const FILTER_STORAGE_KEY = 'directory_filters';
const FILTER_TTL_MS = 60 * 60 * 1000;

function normalizeForSearch(str) {
  return (str || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

// The district of the office an elected official currently holds (staff in that office don't represent it)
function electedDistrict(user) {
    return user.JobExperiences.find(job => job.office_type === "Elected" && job.Division?.district)?.Division.district || "";
}

// Districts of the offices someone currently works in: an elected official and the staff of that office
// (every job in a Member office belongs to that member's district)
function officeDistricts(user) {
    return [...new Set(user.JobExperiences.map(job => job.Division?.district).filter(Boolean))];
}

// A district search lists the official who represents the district ahead of their staff
function representsSearchedDistrict(user, searchParts) {
    const district = electedDistrict(user);
    return !!district && searchParts.some(part => matchesDistrict(part, district));
}

function loadSavedFilters() {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.savedAt > FILTER_TTL_MS) {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
// import AdComponent from "../components/AdComponent";
// import BackToTopButton from "../components/BackToTop";


// MAIN FUNCTION
export default function Directory() {
    // List of states used for Directory page
    const [activeTab, setActiveTab] = useState(() => loadSavedFilters()?.activeTab ?? "people");
    const [searchQuery, setSearchQuery] = useState(() => loadSavedFilters()?.searchQuery ?? "");
    const [filterSelect, setFilterSelect] = useState(() => loadSavedFilters()?.filterSelect ?? "");
    const [issueSelect, setIssueSelect] = useState(() => loadSavedFilters()?.issueSelect ?? undefined);
    const [displayedResults, setDisplayedResults] = useState(30);
    const [cardInfo, setCardInfo] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [issuesInfo, setIssuesInfo] = useState([]);
    const [orgGroups, setOrgGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showSearchHelp, setShowSearchHelp] = useState(false);
    const searchHelpRef = useRef();

    // IntersectionObserver for infinite scroll — works in both portrait and landscape
    const scrollObserver = useRef();
    const sentinelRef = useCallback(node => {
        if (scrollObserver.current) scrollObserver.current.disconnect();
        if (!node) return;
        scrollObserver.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                setDisplayedResults(prev => prev + 30);
            }
        }, { rootMargin: '300px' });
        scrollObserver.current.observe(node);
    }, []);


    useEffect(() => {
        const fetchDirectoryProfiles = async () => {
            setLoading(true);
            const data = await API.getDirectoryProfiles();
            
            data.data.forEach(profile => {
                profile.JobExperiences = profile.JobExperiences.filter(job => job.current_job === true)
            })

            setCardInfo(data.data);
            setLoading(false);
        };

        const fetchOrganizations = async () => {
            const data = await API.getDirectoryOrganizations();

            // Filter out the organization with the name "'elloGovna"
            const filteredData = data.data.filter(org => org.organization_name !== "'elloGovna");
            // Sort the data alphabetically by organization_name
            const sortedData = filteredData.sort((a, b) => a.organization_name.localeCompare(b.organization_name));

            setOrganizations(sortedData);
        };

        const fetchIssueAreas = async () => {
            const data = await API.getIssueAreas();
            setIssuesInfo(data.data.sort((a, b) => a.issue_name.localeCompare(b.issue_name))); 
        };

        const fetchOrganizationGroups = async () => {
            const data = await API.getOrganizationGroups();
            setOrgGroups(data.data);
        }

        fetchDirectoryProfiles();
        fetchOrganizations();
        fetchIssueAreas();
        fetchOrganizationGroups();
    }, []);

    useEffect(() => {
        if (!showSearchHelp) return;
        const closeOnOutside = (e) => {
            if (searchHelpRef.current && !searchHelpRef.current.contains(e.target)) setShowSearchHelp(false);
        };
        document.addEventListener("mousedown", closeOnOutside);
        return () => document.removeEventListener("mousedown", closeOnOutside);
    }, [showSearchHelp]);

    useEffect(() => {
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({ activeTab, searchQuery, filterSelect, issueSelect, savedAt: Date.now() }));
    }, [activeTab, searchQuery, filterSelect, issueSelect]);

    useEffect(() => {
        return () => {
            const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (!raw) return;
            try {
                const saved = JSON.parse(raw);
                sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({ ...saved, searchQuery: '' }));
            } catch {}
        };
    }, []);

    // Function to handle clearing filters and search
    const handleResetFilters = () => {
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
        setFilterSelect("");
        setSearchQuery("");
        setIssueSelect(undefined);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setDisplayedResults(30);
        setFilterSelect("");
        setIssueSelect(undefined);
    };

    // Filtering through the imported results. Make sure to update source once connecting to API / database
    // "AD 62", "sd #27", "assembly district 62" etc. become one word ("ad62") before the query is split
    const userSearchParts = normalizeForSearch(normalizeDistrictQuery(searchQuery)).trim().split(" ");

    const searchUsers = cardInfo.filter(user => {
        const searchParts = userSearchParts;

        const districts = officeDistricts(user);

        const matchesSearch = searchParts.every(part =>
            isDistrictFiller(part) ||
            districts.some(district => matchesDistrict(part, district)) ||
            normalizeForSearch(user.first_name).includes(part) ||
            normalizeForSearch(user.last_name).includes(part) ||
            normalizeForSearch(user.JobExperiences[0]?.title).includes(part) ||
            normalizeForSearch(user.JobExperiences[0]?.Organization.organization_name).includes(part) ||
            normalizeForSearch(user.JobExperiences[0]?.Division?.division_name).includes(part)
        );

        const matchesFilter = filterSelect === "" || user.JobExperiences[0]?.Organization.group_type === filterSelect || 
            (Array.isArray(user.JobExperiences[0]?.Organization.group_type) && 
            user.JobExperiences[0]?.Organization.group_type.some(group_type => filterSelect.includes(group_type))) ||
            (filterSelect === "Electeds" && (
                user.JobExperiences[0]?.title === "Senator" || 
                user.JobExperiences[0]?.title === "President pro Tempore" ||
                user.JobExperiences[0]?.title === "Assemblymember" ||
                user.JobExperiences[0]?.title === "Speaker" ||
                user.JobExperiences[0]?.title === "Governor" ||
                user.JobExperiences[0]?.title === "Lt. Governor" ||
                user.JobExperiences[0]?.title === "Lieutenant Governor" ||
                user.JobExperiences[0]?.title === "State Treasurer" ||
                user.JobExperiences[0]?.title === "Insurance Commissioner" ||
                user.JobExperiences[0]?.title === "State Controller" 
            ));

        const issueSelectAsInt = parseInt(issueSelect, 10);
        let matchesIssue = issueSelect === undefined || user.UserIssues.some(issue => issue.issue_id === issueSelectAsInt);
        

        return matchesSearch && matchesFilter && matchesIssue;
    }).sort((a, b) => Number(!representsSearchedDistrict(a, userSearchParts)) - Number(!representsSearchedDistrict(b, userSearchParts)));

    // Filtering through organizations
    const searchOrgs = organizations.filter(org => {
        const searchParts = normalizeForSearch(searchQuery).split(" ");

        const matchesSearch = searchParts.every(part =>
            normalizeForSearch(org.organization_name).includes(part)
        );

         const normalizedGroupType = (org.group_type || "").trim();

        const matchesFilter =
            filterSelect === "" ||
            (filterSelect === "Other"
                ? (
                    normalizedGroupType === "Other" ||
                    normalizedGroupType === "Asset Manager" ||
                    normalizedGroupType === "" ||
                    normalizedGroupType === null ||
                    normalizedGroupType === undefined
                )
                : normalizedGroupType === filterSelect
            );

        return matchesSearch && matchesFilter;
    });
    
    return (
        <>
            <PageTitle title="Directory | 'elloGovna" />
            <div className="row directory-row">
                    {/* <div className="dir-header"/>  */}
                <div className="col-xl-2 col-lg-3 col-md-3 col-sm-12 ps-md-0 ps-lg-4 directory-left-column">
                    <div className="directory-search-div z-3">
                        <div className="directory-search-wrap mb-2" ref={searchHelpRef}>
                            <input className="form-control directory-search directory-search--help" type="text" placeholder="Search term" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                            <button
                                type="button"
                                className="directory-search-help-btn"
                                aria-label="What can I search for?"
                                aria-expanded={showSearchHelp}
                                onClick={() => setShowSearchHelp(v => !v)}
                            >
                                <i className="bi bi-info-circle" />
                            </button>
                            {showSearchHelp && (
                                <div className="directory-search-help" role="dialog">
                                    {activeTab === "people" ? (
                                        <>
                                            <div className="directory-search-help-title">Search people by</div>
                                            <ul>
                                                <li>Name</li>
                                                <li>Job title</li>
                                                <li>Organization or office</li>
                                                <li>
                                                    Legislative district, to find the elected official and the staff in that office:
                                                    <span className="directory-search-help-example">AD62</span>
                                                    <span className="directory-search-help-example">sd 27</span>
                                                    <span className="directory-search-help-example">Assembly District 62</span>
                                                    <span className="directory-search-help-example">27</span>
                                                    A number alone finds both the Assembly and Senate district.
                                                </li>
                                            </ul>
                                            <div className="directory-search-help-note">Use several words to narrow the results.</div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="directory-search-help-title">Search organizations by</div>
                                            <ul>
                                                <li>Organization name</li>
                                            </ul>
                                            <div className="directory-search-help-note">Use several words to narrow the results.</div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="directory-toggle-container mb-4">
                            <button className={`directory-toggle-btn ${activeTab === 'people' ? 'active' : ''}`} onClick={() => handleTabChange('people')}>People</button>
                            <button className={`directory-toggle-btn ${activeTab === 'organizations' ? 'active' : ''}`} onClick={() => handleTabChange('organizations')}>Organizations</button>
                        </div>
                        {!loading && (
                            <button
                                className="btn btn-sm btn-outline-secondary d-md-none directory-filters-toggle"
                                type="button"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                Filters {showFilters ? <i className="bi bi-chevron-up"></i> : <i className="bi bi-chevron-down"></i>}
                            </button>
                        )}
                        {!loading && (
                            <div className={`row directory-filter-div px-2 mt-3 ${showFilters ? 'd-flex' : 'd-none'} d-md-flex`}>
                                <select
                                    className="form-select directory-filter-select mb-3"
                                    value={filterSelect}
                                    onChange={(e) => setFilterSelect(e.target.value)}
                                >
                                    <option value="" disabled>Select Group</option>
                                    {activeTab === "people" && <option value="Electeds">Elected Officials</option>}
                                    {[...orgGroups]
                                        .sort((a, b) => a.group_name.localeCompare(b.group_name))
                                        .map((group) =>
                                            group.group_name !== "Campaigns" ? (
                                                <option key={group.group_name} value={group.group_name}>
                                                    {group.group_name}
                                                </option>
                                            ) : null
                                        )}
                                    <option value="Other">Other</option>
                                </select>

                                {/* Place Issues drop down here */}
                                {activeTab === "people" && (
                                    <select className="form-select directory-filter-select mb-3" value={issueSelect !== undefined ? issueSelect : ""} onChange={(e) => setIssueSelect(e.target.value)}>
                                        <option value="" disabled>Select Issue Area</option>
                                        {issuesInfo.map(issue => (
                                            <option key={issue.issue_id} value={issue.issue_id}>{issue.issue_name}</option>
                                        ))}
                                    </select>
                                )}
                                <button
                                    className={`clear-filter-button${filterSelect === "" && issueSelect === undefined && searchQuery === "" ? " clear-filter-button--inactive" : ""}`}
                                    onClick={handleResetFilters}
                                    disabled={filterSelect === "" && issueSelect === undefined && searchQuery === ""}
                                >Reset</button>
                                {/* <div className="mt-3">
                                    <AdBox />
                                </div> */}
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-lg-7 col-md-8 col-sm-12 ps-md-4">
                    <div className="directory-body">
                        <div className="row directory-cards">
                            {activeTab === "people" && (
                                searchUsers.slice(0, displayedResults).map((user, index) => (
                                    // Pass user data as props to UserCard component
                                    <DirectoryCard 
                                        key={user.profile_id} 
                                        profile_id={user.profile_id} 
                                        firstName={user.first_name}
                                        lastName={user.last_name}
                                        title={user.JobExperiences[0]?.title}
                                        district={electedDistrict(user)}
                                        organization={user.JobExperiences[0]?.Organization.organization_name}
                                        division={user.JobExperiences[0]?.Division?.division_name || ""}
                                        profile_picture={user.profile_picture}
                                        group_type={user.JobExperiences[0]?.Organization.group_type}
                                    />
                                ))
                            )}

                            {activeTab === "organizations" && (
                                searchOrgs.slice(0, displayedResults).map((org, index) => (
                                    <OrganizationCard 
                                        key={org.organization_id} 
                                        organization_id={org.organization_id} 
                                        organization_name={org.organization_name}
                                        street_address={org.street_address}
                                        suite={org.suite_number}
                                        city={org.city}
                                        state={org.state}
                                        main_phone={org.main_phone}
                                        logo={org.logo}
                                        group_type={org.group_type}
                                    />
                                ))
                            )}
                            
                        </div>
                        {/* Sentinel for IntersectionObserver infinite scroll.
                            Rendered only while more results remain; its removal disconnects the observer. */}
                        {(activeTab === "people"
                            ? displayedResults < searchUsers.length
                            : displayedResults < searchOrgs.length) && (
                            <div ref={sentinelRef} />
                        )}
                        {loading && cardInfo.length === 0 && organizations.length === 0 && (
                            <div className="d-flex position-absolute top-50 start-50 translate-middle ">
                                <div className="spinner-border loading-spinner" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
                {/* <BackToTopButton /> */}
            
           
        </>
    )
}